// 最終形態之後 (D-253): the star → moon → sun marks a companion earns every 5 levels past Lv.30, the aura under a pet that
// holds them, and the words for them. Rules live in src/pet-stars.ts; the moment that announces a new mark is in
// pet-star-cue.tsx (kept apart so light pages like the companion card stay light).
// Everything here is drawn from the pet's box, never per species: the marks float above the box, the aura sits on its floor.
import React from "react";
import { FIRST_MARK_LEVEL, MARK_EVERY, markKindAt, marksOf, MAX_MARK_STEPS, type MarkKind } from "../../src/pet-stars";
import { formatList, levelLabel, t } from "./i18n";
import "./pet-stars.css";

const ICONS: Record<MarkKind, React.ReactNode> = {
  star: <path d="M12 2.6l2.8 5.8 6.3.9-4.6 4.4 1.1 6.3L12 17l-5.6 3 1.1-6.3-4.6-4.4 6.3-.9z" />,
  moon: <path d="M15.6 3.2A9 9 0 1 0 20.8 16 7.2 7.2 0 0 1 15.6 3.2z" />,
  sun: <><path className="pet-mark-rays" d="M12 1.4v3.4M12 19.2v3.4M1.4 12h3.4M19.2 12h3.4M4.5 4.5l2.4 2.4M17.1 17.1l2.4 2.4M4.5 19.5l2.4-2.4M17.1 6.9l2.4-2.4" /><circle cx="12" cy="12" r="5.3" /></>,
};

export function MarkIcon({ kind, className = "" }: { kind: MarkKind; className?: string }) {
  return <svg className={`pet-mark pet-mark-${kind} ${className}`} viewBox="0 0 24 24" aria-hidden="true" focusable="false">{ICONS[kind]}</svg>;
}

/** "2 個月亮、1 顆星": what a screen reader hears for the marks. */
export function marksLabel(steps: number): string {
  const marks = marksOf(steps);
  return formatList([
    ...(marks.suns ? [t("{count} 個太陽", { count: marks.suns })] : []),
    ...(marks.moons ? [t("{count} 個月亮", { count: marks.moons })] : []),
    ...(marks.stars ? [t("{count} 顆星", { count: marks.stars })] : []),
  ]);
}

/** The marks in a row (sun, moons, stars); nothing below the first. Sized by the surrounding font size.
 *  `compact` draws only the biggest mark (roster cards, tray chips): a class full of maxed pets stays calm. */
export function PetMarks({ steps, className = "", compact = false }: { steps: number; className?: string; compact?: boolean }) {
  const marks = marksOf(steps);
  if (!marks.tier) return null;
  const icons: MarkKind[] = compact ? [marks.tier] : [...Array<MarkKind>(marks.suns).fill("sun"), ...Array<MarkKind>(marks.moons).fill("moon"), ...Array<MarkKind>(marks.stars).fill("star")];
  return <span className={`pet-marks pet-marks-${marks.tier} ${className}`} role="img" aria-label={marksLabel(steps)}>{icons.map((kind, i) => <MarkIcon key={i} kind={kind} />)}</span>;
}

/** D-258: a ring on the pet's floor (gold star, silver-blue moon, orange-gold sun with a second ring); colour follows the biggest mark. */
export function PetAura({ steps }: { steps: number }) {
  const tier = marksOf(steps).tier;
  return tier ? <span className={`pet-aura pet-aura-${tier}`} aria-hidden="true" /> : null;
}

/** The title a child's companion carries from its first mark (a word kids can say out loud: "mine is a moonlight companion"). */
export function markTitle(steps: number): string | null {
  const tier = marksOf(steps).tier;
  return tier === "sun" ? t("太陽夥伴") : tier === "moon" ? t("月光夥伴") : tier === "star" ? t("星光夥伴") : null;
}

/** "再 12 點得到下一顆星" and its moon / sun versions: the next goal past the final form. */
export function nextMarkText(points: number, kind: MarkKind): string {
  return kind === "sun" ? t("再 {count} 點換成太陽", { count: points }) : kind === "moon" ? t("再 {count} 點換成月亮", { count: points }) : t("再 {count} 點得到下一顆星", { count: points });
}

/** D-258 徽章之路: the 12 marks past the final form in a row (lit when earned, the next one dashed with the points left),
 *  a bar toward the next one, and an optional goal line (the child's page says the points left in its own hint above, so no line there;
 *  the cells never carry the points: translated, they don't fit a 6-column road on a 320px phone). `toNext` / `progress` are null after the sun. `wrap` 6 lays it out in two rows. */
export function BadgeRoad({ steps, toNext, progress, goal, title, wrap = 12 }: { steps: number; toNext: number | null; progress: number | null; goal?: string; title: string; wrap?: 6 | 12 }) {
  const next = toNext === null ? null : steps + 1;
  const from = 30 + steps * MARK_EVERY;
  return <section className={`mark-road mark-road-${wrap}`} aria-label={title}>
    <h3>{title}</h3>
    {goal && <p className="mark-road-goal">{goal}</p>}
    {next !== null && progress !== null && <div className="mark-road-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)} aria-label={t("下一個徽章")} aria-valuetext={`${t("{from} → {to}", { from: levelLabel(from), to: levelLabel(from + MARK_EVERY) })} · ${t("再 {count} 點", { count: toNext! })}`}>
      <i className={`mark-road-fill-${markKindAt(next)}`} style={{ width: `${Math.max(4, Math.min(100, progress * 100))}%` }} />
      <small aria-hidden="true">{levelLabel(from)} → {levelLabel(from + MARK_EVERY)}</small>
    </div>}
    <ol>{Array.from({ length: MAX_MARK_STEPS }, (_, i) => {
      const n = i + 1, kind = markKindAt(n), got = n <= steps, level = FIRST_MARK_LEVEL + i * MARK_EVERY;
      const state = got ? t("已得到") : n === next ? t("下一個徽章") : t("還沒得到");
      return <li key={n} className={got ? "got" : n === next ? "next" : undefined} aria-label={`${kind === "sun" ? t("{level} 的太陽", { level: levelLabel(level) }) : kind === "moon" ? t("{level} 的月亮", { level: levelLabel(level) }) : t("{level} 的星星", { level: levelLabel(level) })} · ${state}`}>
        <MarkIcon kind={kind} />
        <small aria-hidden="true">{levelLabel(level)}</small>
      </li>;
    })}</ol>
  </section>;
}
